import React from 'react';
import { motion } from 'framer-motion';

const SkillMap = ({ selectedCategory, onSelectCategory }) => {
    // Pentagon positions
    // Balanced Symmetrical Layout
    const categories = [
        {
            id: 'Programming',
            label: 'Programming',
            line1: 'Python · React · MATLAB',
            line2: 'SQL · Git · Jupyter',
            // Top Center
            position: { top: '0%', left: '50%', transform: 'translate(-50%, 0)' },
            coords: { x: '50%', y: '12%' }
        },
        {
            id: 'Leadership',
            label: 'Leadership',
            line1: 'Strategic Thinking',
            line2: 'Adaptability · Collab',
            // Top Right (Symmetrical)
            position: { top: '30%', right: '0%', transform: 'translate(0, -50%)' },
            coords: { x: '90%', y: '30%' }
        },
        {
            id: 'WetLab',
            label: 'Wet Lab',
            line1: 'EEG · EIS · Rat Models',
            line2: 'Microscopy · Hardware',
            // Bottom Right
            position: { bottom: '0%', right: '5%', transform: 'translate(0, 0)' },
            coords: { x: '80%', y: '88%' }
        },
        {
            id: 'Quant',
            label: 'Quantitative Methods',
            line1: 'Modeling & Simulation',
            line2: 'Signal Proc · Stats',
            // Bottom Left - Large Size
            position: { bottom: '0%', left: '5%', transform: 'translate(0, 0)' },
            coords: { x: '20%', y: '88%' },
            size: '190px'
        },
        {
            id: 'ML',
            label: 'Machine Learning',
            line1: 'Deep Learning · ML',
            line2: 'Vision · Explainability',
            // Top Left (Symmetrical to Leadership)
            position: { top: '30%', left: '0%', transform: 'translate(0, -50%)' },
            coords: { x: '10%', y: '30%' }
        },
    ];

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '600px' }}>
            {/* SVG Lines */}
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
                {/* Perimeter Lines (Pentagon) */}
                {categories.map((cat, index) => {
                    const nextCat = categories[(index + 1) % categories.length];
                    return (
                        <motion.line
                            key={`perimeter-${index}`}
                            x1={cat.coords.x}
                            y1={cat.coords.y}
                            x2={nextCat.coords.x}
                            y2={nextCat.coords.y}
                            stroke="rgba(255, 255, 255, 0.15)"
                            strokeWidth="2"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 1.5, delay: 0.5 }}
                        />
                    );
                })}

                {/* Star/Pentagram Lines (Connect non-adjacent nodes) */}
                {categories.map((cat, index) => {
                    // Connect to node at index + 2 (skipping one)
                    const targetIndex = (index + 2) % categories.length;
                    const targetCat = categories[targetIndex];

                    // Check if either this node or the target is selected to highlight the line
                    const isSelected = selectedCategory === cat.id || selectedCategory === targetCat.id;

                    return (
                        <motion.line
                            key={`star-${index}`}
                            x1={cat.coords.x}
                            y1={cat.coords.y}
                            x2={targetCat.coords.x}
                            y2={targetCat.coords.y}
                            stroke={isSelected ? '#4db5ff' : 'rgba(255, 255, 255, 0.1)'}
                            strokeWidth={isSelected ? 2 : 1}
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 1.5, delay: 0.8 }}
                        />
                    );
                })}
            </svg>

            {/* Category Nodes */}
            {categories.map((cat, index) => (
                <motion.div
                    key={cat.id}
                    className="category-node"
                    style={{
                        position: 'absolute',
                        ...cat.position,
                        borderColor: selectedCategory === cat.id ? '#4db5ff' : '#fff',
                        boxShadow: selectedCategory === cat.id ? '0 0 15px #4db5ff' : 'none',
                        width: cat.size || '170px',
                        height: cat.size || '170px'
                    }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: selectedCategory === cat.id ? 1.1 : 1, opacity: selectedCategory && selectedCategory !== cat.id ? 0.5 : 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.5 }}
                    whileHover={{ scale: 1.1, cursor: 'pointer' }}
                    onClick={() => onSelectCategory(cat.id)}
                >
                    <h4 style={{ marginBottom: '8px', fontSize: '1.1rem', fontWeight: 'bold' }}>{cat.label}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span className="ghost-labels" style={{ fontSize: '0.75rem', color: '#ddd' }}>{cat.line1}</span>
                        <span className="ghost-labels" style={{ fontSize: '0.75rem', color: '#aaa' }}>{cat.line2}</span>
                    </div>
                </motion.div>
            ))}
        </div>
    );
};

export default SkillMap;
